import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Form, Modal } from 'antd';
import ModalFormFooter from '../common/ModalFormFooter';
import {
	capexAssociationRestCrudThunksForBuyer,
	capexAssociationRestCrudThunksForSupplier,
	deleteCapexCategoryAssociation,
} from '../../thunks/capex_association_thunks';
import {
	rmAssociationRestCrudThunksForBuyer,
	rmAssociationRestCrudThunksForSupplier,
	deleteRmCategoryAssociation,
} from '../../thunks/rm_association_thunks';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import ExpenseCategoryInput from '../expense_category_input/ExpenseCategoryInput';
import { ROLE_TYPES } from '../../utils/DataConstants';

const CATEGORY_CONFIG = {
	capex: {
		title: 'Add CapEx Category',
		label: 'CapEx Category',
		categoryIdField: 'capExCategoryId',
		categoryField: 'capExCategory',
		okText: 'Save Category',
		cancelText: 'Skip',
	},
	rm: {
		title: 'Add R&M Category',
		label: 'R&M Category',
		categoryIdField: 'rmCategoryId',
		categoryField: 'rmCategory',
		okText: 'Save Category',
		cancelText: 'Skip',
	},
};

interface ExpenseCategoryAdditionalInfoPopupProps {
	categoryType: 'capex' | 'rm';
	onCancel: any;
	saveAssociation?: any;
	deleteAssociation?: any;
	associationId?: any;
	workOrder: any;
	onSuccess: any;
}

const ExpenseCategoryAdditionalInfoPopup: FC<any> = ({
	categoryType,
	onCancel,
	saveAssociation,
	deleteAssociation,
	associationId,
	workOrder,
	onSuccess,
}): React.ReactElement => {
	const config = CATEGORY_CONFIG[categoryType];
	const [saving, setSaving] = useState(false);

	const FORM_ID = `${categoryType}-category-form`;

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);

			const categoryId = nullSafeGet(`name.${config.categoryIdField}`, values);
			const category = nullSafeGet(`name.${config.categoryField}`, values);

			// When the category has been cleared, remove the existing association instead of
			// persisting an empty one — this makes the category setup reversible (OPE-5067).
			const request = category
				? saveAssociation({
						workOrderId: workOrder.id,
						...(categoryId && { [config.categoryIdField]: categoryId }),
						[config.categoryField]: category,
				  })
				: associationId
				? deleteAssociation(associationId)
				: Promise.resolve(true);

			request.then((res) => onSuccess(res)).finally(() => setSaving(false));
		},
		[onSuccess, saveAssociation, deleteAssociation, associationId, workOrder, config]
	);

	return (
		<Modal visible={true} title={config.title} onCancel={onCancel} footer={null} closable={true}>
			<Form
				id={FORM_ID}
				layout="vertical"
				initialValues={{}}
				requiredMark={false}
				onFinish={handleSubmit}
			>
				<Form.Item
					name="name"
					label={config.label}
					initialValue={{
						[config.categoryIdField]: workOrder[config.categoryIdField],
						[config.categoryField]: workOrder[config.categoryField],
					}}
				>
					<ExpenseCategoryInput categoryType={categoryType} />
				</Form.Item>
				<ModalFormFooter
					okText={config.okText}
					cancelText={config.cancelText}
					loading={saving}
					formId={FORM_ID}
					onCancelClick={onCancel}
				/>
			</Form>
		</Modal>
	);
};

const SAVE_MAP = {
	capex: {
		[ROLE_TYPES.BUYER]: capexAssociationRestCrudThunksForBuyer,
		[ROLE_TYPES.SUPPLIER]: capexAssociationRestCrudThunksForSupplier,
	},
	rm: {
		[ROLE_TYPES.BUYER]: rmAssociationRestCrudThunksForBuyer,
		[ROLE_TYPES.SUPPLIER]: rmAssociationRestCrudThunksForSupplier,
	},
};

const DELETE_MAP = {
	capex: deleteCapexCategoryAssociation,
	rm: deleteRmCategoryAssociation,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	saveAssociation: (entity) =>
		dispatch(SAVE_MAP[ownProps.categoryType][ownProps.userType].create(entity)),
	deleteAssociation: (id) => dispatch(DELETE_MAP[ownProps.categoryType](ownProps.userType)(id)),
});

const ComponentWithoutUserType = withRouter(
	connect(null, mapDispatchToProps)(ExpenseCategoryAdditionalInfoPopup)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
